/**
 * Capability 4: Consumer Education
 * Purpose: Builds consumer understanding through learning, assessments and AI assistance.
 */
import { useEffect, useState } from "react";
import CapabilityLandingPage from "@/components/capability/CapabilityLandingPage";
import api from "@/lib/api";
import {
  GraduationCap, ClipboardList, Sparkles, Award, Megaphone, BarChart3,
} from "lucide-react";

export default function ConsumerEducationCapability() {
  const [kpis, setKpis] = useState({ items: null, corpus: null, assessments: null });

  useEffect(() => {
    (async () => {
      const next = { items: null, corpus: null, assessments: null };
      try { const { data } = await api.get("/corpus"); next.corpus = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/learning/items"); next.items = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/assessments/items"); next.assessments = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      setKpis(next);
    })();
  }, []);

  return (
    <CapabilityLandingPage
      eyebrow="Capability 4 · Consumer Education"
      title="Consumer Education"
      purpose="Builds consumer understanding through learning, assessments and AI assistance. Every product decision is anchored in evidence that the consumer understood what they signed."
      primaryActions={[
        { label: "Open Learning Hub", to: "/learning", icon: GraduationCap },
        { label: "Manage NAVI Corpus", to: "/dev/corpus", icon: Sparkles },
      ]}
      kpis={[
        { label: "Learning items", value: kpis.items, subLabel: "in library" },
        { label: "NAVI corpus items", value: kpis.corpus, subLabel: "grounding docs" },
        { label: "Assessments", value: kpis.assessments, subLabel: "published" },
        { label: "Feedback score", value: null, emptyHint: "Tracked per corpus item" },
      ]}
      subCapabilities={[
        { name: "Learning Hub", to: "/learning", icon: GraduationCap, status: "mock",
          description: "Curated lessons, product-typed video training injected into journeys." },
        { name: "Assessments", to: "/assessments", icon: ClipboardList, status: "mock",
          description: "Understanding checks after learning — evidence of comprehension." },
        { name: "NAVI", to: "/dev/corpus", icon: Sparkles, status: "live",
          description: "Grounding corpus and scam-sample library that powers AI briefs." },
        { name: "Certificates", to: "/education-certificates", icon: Award, status: "placeholder",
          description: "Learning + Assessment completion certificates (Ed25519 signed)." },
        { name: "Campaigns", to: "/campaigns", icon: Megaphone, status: "future",
          description: "Targeted literacy campaigns per cohort and product type." },
        { name: "Analytics", to: "/education-analytics", icon: BarChart3, status: "future",
          description: "Comprehension, drop-off, completion, sentiment across cohorts." },
      ]}
      relatedCapabilities={[
        { name: "Trust Workflows · Learning Requests", to: "/capability/trust-workflows" },
        { name: "Consumer Intelligence", to: "/capability/consumer-intelligence" },
      ]}
    />
  );
}
